import { Link } from 'gatsby';

import Badge from 'molecules/Badge';
import Breadcrumbs from 'molecules/Breadcrumbs';
import Image from 'molecules/Image';
import Section from 'molecules/Section';

import HubspotForm from 'components/Form';
import PostDetail from 'components/Hero/PostHero/PostDetail';
import { CaseStudyDetailRow, CaseStudyPostDetail } from 'components/Hero/PostHero/PostDetail/CaseStudy';
import { generateBreadcrumbs } from 'components/Hero/PostHero/utils/helpers';

import calculateReadTime from 'utils/calculateReadTime';
import { createSlugFromName } from 'utils/createSlugFromName';
import richTextParser from 'utils/richTextParser/richTextParser';

import type { ContentfulTemplateCaseStudy } from 'graphqlTypes';
import type { StaticImage } from 'molecules/Image';
import type { FC } from 'react';
import type { PostUnion } from 'types';
import type { RichText } from 'utils/richTextParser/richTextParser';

interface PostHeroProps {
  post: PostUnion;
}

const PostHero: FC<PostHeroProps> = ({ post }) => {
  const { title, tags, featuredImage, hubspotForm, displayBreadcrumbs, contentful_id } = post;

  const breadcrumbs = generateBreadcrumbs(post);
  const readTime = calculateReadTime(post.body as RichText);
  const useCaseStudyTemplate = (post as ContentfulTemplateCaseStudy).companyInfo;

  return (
    <Section id="hero-section" background="textureLight" padding="small" contentfulId={contentful_id}>
      <div className="flex flex-col gap-8 md:gap-12 xl:gap-16">
        <Breadcrumbs visible={displayBreadcrumbs} breadcrumbs={breadcrumbs} isDarkTheme={false} />
        <div
          className={`flex ${hubspotForm ? 'flex-col-reverse' : 'flex-col'} ${useCaseStudyTemplate ? 'xl:items-center' : 'xl:items-start'} gap-8 sm:gap-12 xl:grid xl:grid-cols-2 xl:gap-8`}
        >
          <div className="flex flex-col gap-8 xl:gap-12">
            <div className="flex flex-col items-center gap-6 xl:items-start">
              {tags && tags.length > 0 && (
                <div className="flex flex-wrap justify-center gap-4 xl:justify-start">
                  {tags?.map(
                    tag =>
                      tag?.name && (
                        <Link
                          key={breadcrumbs?.[0]?.link}
                          to={`${breadcrumbs?.[0]?.link}/${createSlugFromName(tag?.name)}#listing`}
                        >
                          <Badge key={tag?.id} label={tag?.name} size="lg" hierarchy="primary" />
                        </Link>
                      ),
                  )}
                </div>
              )}
              {title && (
                <h1 className="text-center text-display-md-mobile font-extrabold text-gray-900 md:text-display-md-tablet xl:text-left xl:text-display-md">
                  {title}
                </h1>
              )}
              {useCaseStudyTemplate && (
                <div>{richTextParser((post as ContentfulTemplateCaseStudy).companyInfo as RichText)}</div>
              )}
            </div>
            {useCaseStudyTemplate && (
              <>
                <CaseStudyDetailRow>
                  <CaseStudyPostDetail
                    icon="business"
                    label="Business Type"
                    value={(post as ContentfulTemplateCaseStudy).businessType}
                  />
                  <CaseStudyPostDetail
                    icon="globe"
                    label="Website"
                    value={(post as ContentfulTemplateCaseStudy).webSite}
                  />
                </CaseStudyDetailRow>
                <CaseStudyDetailRow>
                  <CaseStudyPostDetail
                    icon="alert"
                    label="Pain Point"
                    value={(post as ContentfulTemplateCaseStudy).painPoint}
                  />
                </CaseStudyDetailRow>
              </>
            )}
            {!useCaseStudyTemplate && (
              <div className="mx-auto flex w-max flex-col items-center gap-4 md:flex-row lg:gap-6 xl:mx-0">
                <PostDetail
                  className="items-center"
                  icon="access-time"
                  label="Read Time"
                  value={`${readTime} minutes`}
                />
                <PostDetail
                  className="items-center"
                  icon="edit"
                  label="Author"
                  value={post?.author?.fullName}
                  link={`/author/${createSlugFromName(post?.author?.fullName)}`}
                />
                <PostDetail className="items-center" icon="calendar-today" label="Published" value={post.publishDate} />
              </div>
            )}
          </div>
          {hubspotForm && (
            <div className="bg-white p-6 shadow-xl">{hubspotForm && <HubspotForm {...hubspotForm} />}</div>
          )}
          {!hubspotForm && featuredImage && (
            <Image
              image={featuredImage as StaticImage}
              alt={title || 'Card Image'}
              className="aspect-video object-cover"
            />
          )}
        </div>
      </div>
    </Section>
  );
};

export default PostHero;
